import * as React from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { Text } from 'DVWebNodeUI'
import * as st from './playback.module.scss'
import { tryCatch } from '../../utils/tryCatch'
import { RecordRefTag } from '../recordReftag/recordReftag'
import { getPlaybackClickstreamRef } from '../../utils/getPlaybackClickstreamRef'
import { getPlaybackProgress } from '../../utils/getPlaybackProgress'
import {
  DeeplinkProps,
  PlayBox,
  SquarePlayButton,
} from '../playButton/PlayButton'
import { MaybeSpaContextProps, withMaybeSpaContext } from '@shared'
import { DetailPageActions } from '../../actions'
import { startPlayback } from '../../actions/player'
import { PlaybackContentLink } from '../../integrations/playerIntegration'

export interface IPlaybackProps {
  playbackData?: WpsRuntime.atv.wps.PlaybackActionDataProps
  sessionId: string
  pageTitleId: string
  usePlayer: boolean
  features?: string | string[]
  isRetail?: boolean
  isHover?: boolean
  isStartOver?: boolean
}

type PlaybackWithSpaContextProps = IPlaybackProps & MaybeSpaContextProps & ReturnType<typeof mapDispatchToProps>

type Handler = undefined | ((e: React.MouseEvent) => void)

const buildSquarePlayButton = (
  deeplinkingData: DeeplinkProps,
  playbackData: WpsRuntime.atv.wps.PlaybackActionDataProps,
  handler: Handler,
  isStartOver?: boolean
) => (
  <SquarePlayButton
    deepLinkingData={deeplinkingData}
    href={playbackData.playbackURL}
    onClick={handler}
    progress={getPlaybackProgress(playbackData.progress)}
    forceDoubleHeight={true}
    isStartOver={isStartOver}
  >
    <Text tag="span" string={playbackData.label} />
  </SquarePlayButton>
)

const buildCirclePlayButton = (
  deeplinkingData: DeeplinkProps,
  playbackData: WpsRuntime.atv.wps.PlaybackActionDataProps,
  isHover: boolean,
  handler: Handler,
  isStartOver?: boolean
) => (
  <PlayBox
    deepLinkingData={deeplinkingData}
    href={playbackData.playbackURL}
    onClick={handler}
    progress={getPlaybackProgress(playbackData.progress)}
    isLarge={!isHover}
    isStartOver={isStartOver}
    isHover={isHover}
  >
    <Text tag="span" string={playbackData.label} />
  </PlayBox>
)

export const ConnectedPlayback = tryCatch(
  ({
    playbackData,
    sessionId,
    pageTitleId,
    isRetail,
    isStartOver,
    isHover = false,
    usePlayer,
    hasDownloadCompleted,
    playOffline,
    startPlayback,
  }: PlaybackWithSpaContextProps) => {
    if (!playbackData) {
      return null
    }

    const deeplinkingData = {
      fallbackUrl: playbackData.fallbackURL,
      titleId: playbackData.playbackID,
      resumeTime: playbackData.resumeTime,
      signInUrl: playbackData.signInURL,
      sessionId,
      pageTitleId,
      ref: getPlaybackClickstreamRef(
        playbackData.contentType,
        playbackData.entitlementType
      ),
      videoType: playbackData.videoMaterialType,
    }

    const playbackTitleId = playbackData.playbackID

    const triggerOfflinePlayback: Handler = e => {
      playOffline!(playbackTitleId)
      e.preventDefault()
    }

    const triggerOnlinePlayback: Handler = e => {
      const contentConfig = {
        asin: playbackTitleId,
        trailer: false,
        position: playbackData.resumeTime,
        ref: getPlaybackClickstreamRef(
          playbackData.contentType,
          playbackData.entitlementType
        ),
        videoType: playbackData.videoMaterialType,
        useFullView: true,
      }

      startPlayback({
        contentConfig,
        signInUrl: playbackData.signInURL,
        playbackUrl: playbackData.playbackURL,
      })
      e.preventDefault()
    }

    const downloadCompleted =
      hasDownloadCompleted && hasDownloadCompleted(playbackTitleId)
    const triggerPlayback = downloadCompleted
      ? triggerOfflinePlayback
      : triggerOnlinePlayback
    const handler = usePlayer ? triggerPlayback : undefined

    const playButton =
      isRetail && !isHover
        ? buildSquarePlayButton(
            deeplinkingData,
            playbackData,
            handler,
            isStartOver
          )
        : buildCirclePlayButton(
            deeplinkingData,
            playbackData,
            isHover,
            handler,
            isStartOver
          )

    return (
      <RecordRefTag
        refTag="atv_dp_pb_main"
        className="avu-full-width-phablet dv-dp-node-playback"
      >
        <div className={st.playback}>{playButton}</div>
      </RecordRefTag>
    )
  }
)

const mapDispatchToProps = (dispatch: Dispatch<DetailPageActions>) => ({
  startPlayback: (playbackContentLink: PlaybackContentLink) =>
    dispatch(startPlayback(playbackContentLink)),
})

export const Playback = connect(null, mapDispatchToProps)(ConnectedPlayback)

export const ActionBoxPlayback = withMaybeSpaContext(Playback)
